import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

import Tour from '../components/tour'
import { addLocalStorage } from '../utils/storage_helpers'
import { useGame } from './game'
import { GameState } from '../../domain/enum/game-state.enum'

export type IStepProps = {
  id: number | string;
  className: string;
  label: string;
  arrowDirection: 'right' | 'top' | 'bottom';
}

export type IOffSetProps = {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
  x: number;
  y: number;
}

type TourContextProps = {
  isOpen: boolean;
  steps: IStepProps[];
  currentStep: IStepProps;
  tourWasCompleted: boolean;
  currentOffset: IOffSetProps | undefined;
  showInstructions: boolean;
  handleToggleModal: () => void;
  handleNextStep: () => void;
  handleToggleTour: () => void;
  isFirstStep: () => boolean;
  isLastStep: () => boolean;
  toggleShowTour: (value:boolean) => void
}

type TourProviderProps = {
  children: ReactNode;
}

const TourContext = createContext({} as TourContextProps)

const stepsDefault: IStepProps[] = [
  {
    id: 1,
    className: 'tour-1',
    label: 'Start the mission here',
    arrowDirection: 'right'
  },
  {
    id: 2,
    className: 'tour-2',
    label: 'Follow the score here',
    arrowDirection: 'top'
  },
  {
    id: 3,
    className: 'tour-3',
    label: 'The time will appear centered when starting the mission',
    arrowDirection: 'top'
  }
]

const approvalSteps: IStepProps[] = [
  {
    id: 1,
    className: 'tourApproval-1',
    label: 'Leave a review by clicking here.',
    arrowDirection: 'right'
  },
  {
    id: 2,
    className: 'tourApproval-2',
    label: 'Approve this mission without a review by clicking here.',
    arrowDirection: 'right'
  }
  // {
  //   id: 3,
  //   className: 'tourApproval-3',
  //   label: 'Return to the last mission by clicking here.',
  //   arrowDirection: 'bottom'
  // }
]

const TourProvider = ({ children }: TourProviderProps) => {
  const [showInstructions, setShowInstructions] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState(false)
  const [steps, setSteps] = useState<IStepProps[]>([])
  const [currentStep, setCurrentStep] = useState<IStepProps>({} as IStepProps)
  const [currentOffset, setCurrentOffset] = useState<IOffSetProps | undefined>({} as IOffSetProps)
  const [tourWasCompleted, setTourWasCompleted] = useState(false)
  const [gameState, setGameState] = useState<any>(null)
  const { game } = useGame()

  function toggleShowTour (value:boolean): void {
    setShowInstructions(value)
  }

  function isFirstStep () {
    return steps[0] === currentStep
  }

  function isLastStep () {
    return steps[steps.length - 1] === currentStep
  }

  function getCurrentStepOffset (): IOffSetProps | undefined {
    const currentEl = document.querySelector(`.${currentStep?.className}`)

    return currentEl?.getBoundingClientRect()
  }

  function toggleHighlightStyle (className: string, isAdding = true): void {
    const stepEls = document.querySelectorAll(`.${className}`)

    setCurrentOffset(getCurrentStepOffset())

    stepEls.forEach(elm => {
      if (isAdding) {
        elm.classList.add('highlight-tour')
      } else {
        elm.classList.remove('highlight-tour')
      }
    })
  }

  function handleToggleModal (): void {
    setIsOpen(!isOpen)
  }

  function handleNextStep (): void {
    const indexCurrentStep = steps.indexOf(currentStep)
    const nextCurrentStep = steps[indexCurrentStep + 1]

    if (isLastStep()) {
      setIsOpen(false)
      setTourWasCompleted(true)
      addLocalStorage('tourWasCompleted', 'true')
      return
    }

    setCurrentStep(_ => nextCurrentStep)
    toggleHighlightStyle(currentStep.className, false)
    toggleHighlightStyle(nextCurrentStep.className)
  }

  function handleToggleTour () {
    if (!tourWasCompleted && showInstructions) {
      if (isFirstStep()) {
        toggleHighlightStyle(currentStep.className)
      }
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
  }

  function loadContextSettings () {
    const wasCompleted = localStorage.getItem('tourWasCompleted')
    setTourWasCompleted(!!wasCompleted)
    if (gameState === GameState.APPROVAL) {
      setCurrentStep(approvalSteps[0])
      setSteps([...approvalSteps])
    }
    if (gameState === GameState.RUNNING || gameState === GameState.TESTING) {
      setCurrentStep(stepsDefault[0])
      setSteps([...stepsDefault])
    }

    setCurrentOffset(getCurrentStepOffset())
  }

  useEffect(() => {
    loadContextSettings()
  }, [gameState])

  useEffect(() => {
    handleToggleTour()
  }, [tourWasCompleted, isOpen])

  useEffect(() => {
    setCurrentOffset(getCurrentStepOffset())
  }, [currentStep])

  useEffect(() => {
    setGameState(game?.gameState)
  }, [game])

  return (
    <TourContext.Provider
      value={{
        handleToggleModal,
        isOpen,
        tourWasCompleted,
        steps,
        currentStep,
        handleNextStep,
        currentOffset,
        isFirstStep,
        isLastStep,
        handleToggleTour,
        toggleShowTour,
        showInstructions
      }}
    >
      {children}
      <Tour />
    </TourContext.Provider>
  )
}

const useTour = () => {
  const context = useContext(TourContext)

  if (!context) throw new Error('useTour must be used within a provider TourProvider')

  return { ...context }
}

export { TourContext, TourProvider, useTour }
